import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Alert,
  Box,
  CircularProgress,
} from "@mui/material";

import Grid2 from "@mui/material/Grid2";

import { useParams } from "react-router-dom";

import GenericStaticSummary from "components/Records/RecordDisplay/GenericStaticSummary";

import PeopleInfoSection from "components/PeopleSummary/PeopleInfoSection";

import AttendanceSection from "./EventLogging/AttendanceSection";

import EventLogFormSection from "./EventLogging/EventLogFormSection";

import { getLinkedPeopleFromInteraction } from "api/Interactions/InteractionAPI";

import {
  eventsAPI,
  eventLogAPI,
} from "api/Interactions/EventsAPI";

import { peopleAPI } from "api/Community/PeopleAPI";

import { moveItemsBetweenNodes } from "api/Campaigns/CampaignAPI";


interface SimplePerson {
  id: number;
  primary: string;
}


interface AttendedPersonRecord {
  logId: number;
  person: any;
  logData: any;
  simple: SimplePerson;
}


const EventLogging: React.FC = () => {
  const { token } = useParams<{
    token: string;
  }>();


  const [
    loading,
    setLoading,
  ] = useState(true);


  const [
    event,
    setEvent,
  ] = useState<any | null>(
    null
  );


  const [
    peopleList,
    setPeopleList,
  ] = useState<
    SimplePerson[]
  >([]);


  const [
    expectedPeopleList,
    setExpectedPeopleList,
  ] = useState<
    SimplePerson[]
  >([]);


  /*
   * Displayed in the first attendance section.
   *
   * This list is rebuilt from actual EventLog records
   * instead of only existing for the current browser session.
   */
  const [
    attendedPeopleList,
    setAttendedPeopleList,
  ] = useState<
    SimplePerson[]
  >([]);


  const [
    attendedPeople,
    setAttendedPeople,
  ] = useState<
    AttendedPersonRecord[]
  >([]);


  const [
    selectedPersonToEdit,
    setSelectedPersonToEdit,
  ] = useState<
    any | null
  >(null);


  const [
    selectedLogToEdit,
    setSelectedLogToEdit,
  ] = useState<
    number | null
  >(null);


  const [
    notes,
    setNotes,
  ] = useState("");


  const [
    attendanceLoadingPersonId,
    setAttendanceLoadingPersonId,
  ] = useState<
    number | null
  >(null);


  const [
    addingPeople,
    setAddingPeople,
  ] = useState(false);


  const [
    selectingPerson,
    setSelectingPerson,
  ] = useState(false);


  const externalAccessToPeopleDatabase =
    event?.external_access_to_people_database ??
    false;


  const mapPerson = useCallback(
    (
      person: any
    ): SimplePerson => ({
      id: Number(
        person.id
      ),

      primary: `${
        person.first_name ??
        ""
      } ${
        person.last_name ??
        ""
      }`.trim(),
    }),

    []
  );


  const attendedPersonIds =
    useMemo(
      () =>
        Array.from(
          new Set(
            attendedPeople.map(
              record =>
                Number(
                  record.simple.id
                )
            )
          )
        ),

      [
        attendedPeople,
      ]
    );


  const selectedPersonId =
    selectedPersonToEdit?.id !==
      undefined &&
    selectedPersonToEdit?.id !==
      null

      ? Number(
          selectedPersonToEdit.id
        )

      : null;


  const selectedPersonIsAttended =
    selectedPersonId !== null &&
    attendedPersonIds.includes(
      selectedPersonId
    );


  const findAttendanceRecord =
    useCallback(
      (
        personId:
          number
      ) =>
        attendedPeople.find(
          record =>
            Number(
              record.simple.id
            ) ===
            Number(
              personId
            )
        ),

      [
        attendedPeople,
      ]
    );


  /*
   * Event logs are the permanent attendance history.
   *
   * Use those logs to rebuild the visible Attended People
   * section whenever the page loads or attendance changes.
   */
  const fetchAttendedPeople =
    useCallback(
      async () => {
        if (!event) {
          return;
        }

        const logs =
          await eventLogAPI.getAllByParent(
            Number(
              event.id
            ),

            token
          );

        const mapped =
          await Promise.all(
            logs.map(
              async (
                log: any
              ) => {
                const personId =
                  log?.people?.id ??
                  log?.people ??
                  log?.people_id ??
                  null;

                if (
                  personId === null ||
                  personId === undefined
                ) {
                  return null;
                }

                const person =
                  token

                    ? await peopleAPI.getByIdWithInteractionToken(
                        Number(
                          personId
                        ),

                        token
                      )

                    : await peopleAPI.getById(
                        Number(
                          personId
                        )
                      );

                return {
                  logId:
                    Number(
                      log.id
                    ),

                  person,

                  logData:
                    log,

                  simple:
                    mapPerson(
                      person
                    ),
                };
              }
            )
          );

        const validRecords =
          mapped.filter(
            Boolean
          ) as AttendedPersonRecord[];

        setAttendedPeople(
          validRecords
        );

        /*
         * Build a de-duplicated historical attendee list
         * directly from the event logs.
         */
        const uniqueAttendees =
          Array.from(
            new Map(
              validRecords.map(
                record => [
                  Number(
                    record.simple.id
                  ),

                  record.simple,
                ]
              )
            ).values()
          );

        setAttendedPeopleList(
          uniqueAttendees
        );
      },

      [
        event,
        mapPerson,
        token,
      ]
    );


  /*
   * Load the event and people currently expected at this
   * pathway step.
   */
  useEffect(
    () => {
      if (!token) {
        return;
      }

      let active =
        true;

      const fetchEvent =
        async () => {
          try {
            const data =
              await eventsAPI.getByToken(
                token
              );

            const linkedPeople =
              await getLinkedPeopleFromInteraction(
                "event",

                Number(
                  data.id
                )
              );

            if (!active) {
              return;
            }

            setExpectedPeopleList(
              linkedPeople.map(
                mapPerson
              )
            );

            setEvent(
              data
            );
          } catch (
            error
          ) {
            console.error(
              "Could not load event:",

              error
            );

            if (
              active
            ) {
              setLoading(
                false
              );
            }
          }
        };

      fetchEvent();

      return () => {
        active =
          false;
      };
    },

    [
      mapPerson,
      token,
    ]
  );


  /*
   * Load the people available through the database picker
   * ONLY when the event allows external database access.
   */
  useEffect(
    () => {
      if (!event) {
        return;
      }

      if (
        !externalAccessToPeopleDatabase
      ) {
        setPeopleList(
          []
        );

        setLoading(
          false
        );

        return;
      }

      let active =
        true;

      const fetchPeople =
        async () => {
          try {
            const list =
              token

                ? await peopleAPI.getAllWithInteractionToken(
                    token
                  )

                : await peopleAPI.getAll();

            if (!active) {
              return;
            }

            setPeopleList(
              list.map(
                mapPerson
              )
            );
          } catch (
            error
          ) {
            console.error(
              "Could not load people:",

              error
            );
          } finally {
            if (
              active
            ) {
              setLoading(
                false
              );
            }
          }
        };

      fetchPeople();

      return () => {
        active =
          false;
      };
    },

    [
      event,
      externalAccessToPeopleDatabase,
      mapPerson,
      token,
    ]
  );


  /*
   * Load historical attendees once the event exists.
   */
  useEffect(
    () => {
      if (!event) {
        return;
      }

      fetchAttendedPeople();
    },

    [
      event,
      fetchAttendedPeople,
    ]
  );


  /*
   * Mark people as attended.
   *
   * moveItemsBetweenNodes() both moves the person through
   * the pathway and causes the backend to create the EventLog.
   */
  const createAttendanceLogs =
    async (
      selectedPeople:
        SimplePerson[]
    ) => {
      if (
        !event ||
        selectedPeople.length ===
          0
      ) {
        return;
      }

      const eventId =
        Number(
          event.id
        );

      const uniquePeople =
        Array.from(
          new Map(
            selectedPeople.map(
              person => [
                Number(
                  person.id
                ),

                {
                  ...person,

                  id: Number(
                    person.id
                  ),
                },
              ]
            )
          ).values()
        );

      const existingLogs =
        await eventLogAPI.getAllByParent(
          eventId,

          token
        );

      const existingPersonIds =
        new Set<number>(
          existingLogs
            .map(
              (
                log: any
              ) =>
                log?.people?.id ??
                log?.people ??
                log?.people_id ??
                null
            )

            .filter(
              (
                id: any
              ) =>
                id !== null &&
                id !== undefined &&
                id !== ""
            )

            .map(
              (
                id: any
              ) =>
                Number(
                  id
                )
            )

            .filter(
              (
                id:
                  number
              ) =>
                !Number.isNaN(
                  id
                )
            )
        );

      const peopleToCreate =
        uniquePeople.filter(
          person =>
            !existingPersonIds.has(
              Number(
                person.id
              )
            )
        );

      if (
        peopleToCreate.length >
        0
      ) {
        const result =
          await moveItemsBetweenNodes(
            {
              item_field_name:
                "people",

              selected_items:
                peopleToCreate.map(
                  person =>
                    Number(
                      person.id
                    )
                ),

              handle_name:
                "output-7",

              direction:
                "forward",

              interaction_id:
                eventId,

              interaction_type:
                "event",
            }
          );

        /*
         * Mirror the successful backend pathway move locally.
         *
         * The people disappear from Expected People and their
         * newly created logs make them appear in Attended People.
         */
        if (
          Number(
            result?.moved ??
              0
          ) > 0
        ) {
          const movedIds =
            new Set(
              peopleToCreate.map(
                person =>
                  Number(
                    person.id
                  )
              )
            );

          setExpectedPeopleList(
            previous =>
              previous.filter(
                person =>
                  !movedIds.has(
                    Number(
                      person.id
                    )
                  )
              )
          );
        }
      }
    };


  const selectPerson =
    async (
      personItem:
        SimplePerson
    ) => {
      setSelectingPerson(
        true
      );

      setSelectedPersonToEdit(
        null
      );

      setSelectedLogToEdit(
        null
      );

      setNotes(
        ""
      );

      try {
        const freshPerson =
          token

            ? await peopleAPI.getByIdWithInteractionToken(
                Number(
                  personItem.id
                ),

                token
              )

            : await peopleAPI.getById(
                Number(
                  personItem.id
                )
              );

        const attendanceRecord =
          findAttendanceRecord(
            Number(
              personItem.id
            )
          );

        if (
          attendanceRecord
        ) {
          const freshLog =
            await eventLogAPI.getById(
              attendanceRecord.logId
            );

          setSelectedLogToEdit(
            Number(
              freshLog.id
            )
          );

          setNotes(
            freshLog?.notes ??
              ""
          );
        } else if (
          event
        ) {
          const logs =
            await eventLogAPI.getAllByParent(
              Number(
                event.id
              ),

              token
            );

          const matchingLog =
            logs.find(
              (
                log: any
              ) =>
                Number(
                  log?.people?.id ??
                  log?.people ??
                  log?.people_id
                ) ===
                Number(
                  personItem.id
                )
            );

          if (
            matchingLog
          ) {
            setSelectedLogToEdit(
              Number(
                matchingLog.id
              )
            );

            setNotes(
              matchingLog?.notes ??
                ""
            );
          }
        }

        setSelectedPersonToEdit(
          freshPerson
        );
      } catch (
        error
      ) {
        console.error(
          "Could not select person:",

          error
        );
      } finally {
        setSelectingPerson(
          false
        );
      }
    };


  const addPeople =
    async (
      selectedPeople:
        SimplePerson[]
    ) => {
      if (
        selectedPeople.length ===
        0
      ) {
        return;
      }

      setAddingPeople(
        true
      );

      try {
        /*
         * New people are treated as attended immediately.
         * Their backend logs become the permanent record.
         */
        await createAttendanceLogs(
          selectedPeople
        );

        await fetchAttendedPeople();

        await selectPerson(
          selectedPeople[0]
        );
      } catch (
        error
      ) {
        console.error(
          "Could not add people:",

          error
        );
      } finally {
        setAddingPeople(
          false
        );
      }
    };


  const removeAttendance =
    async (
      personId:
        number
    ) => {
      let attendanceRecord =
        findAttendanceRecord(
          personId
        );

      if (
        !attendanceRecord &&
        event
      ) {
        const logs =
          await eventLogAPI.getAllByParent(
            Number(
              event.id
            ),

            token
          );

        const matchingLog =
          logs.find(
            (
              log: any
            ) =>
              Number(
                log?.people?.id ??
                log?.people ??
                log?.people_id
              ) ===
              Number(
                personId
              )
          );

        if (
          matchingLog
        ) {
          attendanceRecord =
            {
              logId:
                Number(
                  matchingLog.id
                ),

              person:
                null,

              logData:
                matchingLog,

              simple: {
                id:
                  Number(
                    personId
                  ),

                primary:
                  "",
              },
            };
        }
      }

      if (
        !attendanceRecord
      ) {
        return;
      }

      await eventLogAPI.delete(
        attendanceRecord.logId
      );

      if (
        selectedPersonId ===
        personId
      ) {
        setSelectedLogToEdit(
          null
        );

        setNotes(
          ""
        );
      }
    };


  const setAttendance =
    async (
      person:
        SimplePerson,

      attended:
        boolean
    ) => {
      const personId =
        Number(
          person.id
        );

      setAttendanceLoadingPersonId(
        personId
      );

      try {
        if (
          attended
        ) {
          await createAttendanceLogs(
            [
              person,
            ]
          );
        } else {
          await removeAttendance(
            personId
          );
        }

        /*
         * Rebuild attendance from actual EventLog records.
         */
        await fetchAttendedPeople();

        if (
          selectedPersonId ===
          personId
        ) {
          if (
            attended &&
            event
          ) {
            const logs =
              await eventLogAPI.getAllByParent(
                Number(
                  event.id
                ),

                token
              );

            const newLog =
              logs.find(
                (
                  log: any
                ) =>
                  Number(
                    log?.people?.id ??
                    log?.people ??
                    log?.people_id
                  ) ===
                  personId
              );

            if (
              newLog
            ) {
              setSelectedLogToEdit(
                Number(
                  newLog.id
                )
              );

              setNotes(
                newLog?.notes ??
                  ""
              );
            }
          } else {
            setSelectedLogToEdit(
              null
            );

            setNotes(
              ""
            );
          }
        }
      } catch (
        error
      ) {
        console.error(
          "Could not update attendance:",

          error
        );
      } finally {
        setAttendanceLoadingPersonId(
          null
        );
      }
    };


  const submitForm =
    async () => {
      if (
        !selectedLogToEdit ||
        !event
      ) {
        return;
      }

      const updated =
        await eventLogAPI.update(
          selectedLogToEdit,

          {
            event:
              Number(
                event.id
              ),

            notes,
          }
        );

      setAttendedPeople(
        previous =>
          previous.map(
            record =>
              record.logId ===
              selectedLogToEdit

                ? {
                    ...record,

                    logData: {
                      ...record.logData,

                      notes:
                        updated?.notes ??
                        notes,
                    },
                  }

                : record
          )
      );
    };


  const updateMappedPersonInList =
    (
      people:
        SimplePerson[],

      updatedPerson:
        SimplePerson
    ) =>
      people.map(
        person =>
          Number(
            person.id
          ) ===
          Number(
            updatedPerson.id
          )

            ? updatedPerson

            : person
      );


  return (
    <Box
      sx={{
        width:
          "100%",

        maxWidth:
          1600,

        mx:
          "auto",

        px: {
          xs:
            1,

          sm:
            2,

          md:
            3,
        },

        pb: {
          xs:
            3,

          md:
            4,
        },
      }}
    >
      <GenericStaticSummary
        record={
          event
        }

        titleField="name"

        descriptionField="description"

        loading={
          loading
        }
      />


      <Grid2
        container

        spacing={{
          xs:
            1.5,

          sm:
            2,

          md:
            3,
        }}

        alignItems="flex-start"

        sx={{
          mt: {
            xs:
              0.5,

            sm:
              1,
          },
        }}
      >
        <Grid2
          size={{
            xs:
              12,

            lg:
              5,
          }}
        >
          <AttendanceSection
            addedPeople={
              attendedPeopleList
            }

            expectedPeople={
              expectedPeopleList
            }

            peopleList={
              peopleList
            }

            attendedPersonIds={
              attendedPersonIds
            }

            selectedPersonId={
              selectedPersonId
            }

            loading={
              loading
            }

            attendanceLoadingPersonId={
              attendanceLoadingPersonId
            }

            addingDatabasePeople={
              addingPeople
            }

            externalAccessToPeopleDatabase={
              externalAccessToPeopleDatabase
            }

            addPeople={
              addPeople
            }

            setAttendance={
              setAttendance
            }

            selectPerson={
              selectPerson
            }
          />
        </Grid2>


        <Grid2
          size={{
            xs:
              12,

            lg:
              7,
          }}

          sx={{
            minWidth:
              0,
          }}
        >
          {selectingPerson ? (
            <Box
              sx={{
                minHeight: {
                  xs:
                    140,

                  sm:
                    180,
                },

                display:
                  "flex",

                alignItems:
                  "center",

                justifyContent:
                  "center",
              }}
            >
              <CircularProgress />
            </Box>

          ) : selectedPersonToEdit ? (
            <Box
              sx={{
                minWidth:
                  0,

                display:
                  "flex",

                flexDirection:
                  "column",

                gap: {
                  xs:
                    1.5,

                  sm:
                    2,
                },
              }}
            >
              <PeopleInfoSection
                person={
                  selectedPersonToEdit
                }

                interactionToken={
                  token
                }

                onPersonUpdated={
                  updated => {
                    setSelectedPersonToEdit(
                      updated
                    );

                    const mapped =
                      mapPerson(
                        updated
                      );

                    setPeopleList(
                      previous =>
                        updateMappedPersonInList(
                          previous,

                          mapped
                        )
                    );

                    setExpectedPeopleList(
                      previous =>
                        updateMappedPersonInList(
                          previous,

                          mapped
                        )
                    );

                    setAttendedPeopleList(
                      previous =>
                        updateMappedPersonInList(
                          previous,

                          mapped
                        )
                    );

                    setAttendedPeople(
                      previous =>
                        previous.map(
                          record =>
                            Number(
                              record.simple.id
                            ) ===
                            Number(
                              mapped.id
                            )

                              ? {
                                  ...record,

                                  person:
                                    updated,

                                  simple:
                                    mapped,
                                }

                              : record
                        )
                    );
                  }
                }
              />


              {selectedPersonIsAttended &&
              selectedLogToEdit ? (
                <EventLogFormSection
                  notes={
                    notes
                  }

                  setNotes={
                    setNotes
                  }

                  onSubmit={
                    submitForm
                  }
                />

              ) : (
                <Alert
                  severity="info"

                  sx={{
                    width:
                      "100%",
                  }}
                >
                  Mark this person as attended before adding event notes.
                </Alert>
              )}
            </Box>

          ) : null}
        </Grid2>
      </Grid2>
    </Box>
  );
};


export default EventLogging;